import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IOnuSyncPreview,
  IOnuSyncRow,
  OnuSyncField,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';

/**
 * Sincroniza los datos del cliente y del contrato del sistema hacia SmartOLT
 * (nombre, dirección, contacto y coordenadas). Muestra la diferencia y copia solo lo elegido.
 */
@Component({
  selector: 'app-onu-sync-form',
  standalone: true,
  imports: [FormsModule, ButtonModule, CheckboxModule],
  templateUrl: './onu-sync-form.component.html',
})
export class OnuSyncFormComponent implements OnInit {
  @Input({ required: true }) onuId!: number;
  @Output() synced = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  public preview: IOnuSyncPreview | null = null;
  public loading = true;
  public error = '';
  public saving = false;
  public selected = new Set<OnuSyncField>();

  constructor(private readonly onuViewService: OnuViewService) {}

  ngOnInit(): void {
    this.load();
  }

  /**
   * load: compara con SmartOLT y deja marcado todo lo que difiere
   */
  public async load() {
    this.loading = true;
    this.error = '';
    try {
      const response = await this.onuViewService.syncPreview(this.onuId);
      this.preview = response.data;
      this.selected = new Set(this.preview.rows.filter((row) => row.changed).map((row) => row.key));
    } catch (error: any) {
      this.preview = null;
      this.error =
        error?.error?.messages ??
        error?.error?.data?.message ??
        'No se pudo comparar con el sistema.';
    } finally {
      this.loading = false;
    }
  }

  public toggle(row: IOnuSyncRow, checked: boolean) {
    checked ? this.selected.add(row.key) : this.selected.delete(row.key);
  }

  public get pending(): IOnuSyncRow[] {
    return this.preview?.rows.filter((row) => row.changed) ?? [];
  }

  public status(row: IOnuSyncRow): 'sync' | 'diff' | 'missing' {
    if (!row.available) return 'missing';
    return row.changed ? 'diff' : 'sync';
  }

  /**
   * apply
   */
  public async apply() {
    if (!this.selected.size) return;
    this.saving = true;
    try {
      const response = await this.onuViewService.syncApply(this.onuId, [...this.selected]);
      this.synced.emit(response.message);
    } catch (error) {
      // el interceptor ya mostró el motivo
    } finally {
      this.saving = false;
    }
  }
}
